import { TFunction } from 'i18next';
import { MultiselectOption } from '@cbo/shared-library';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ReoccurrenceType, ScheduledReport, ScheduledReportsTabId, ScheduleStatus } from '../types';
import { User } from '../../models/User';
import { OrganizationUser } from '../../admin/requests/index';
import { SiteInfo } from '../../contexts/siteContext';

dayjs.extend(utc);

// Constants
export const INITIAL_CARDS_COUNT = 6;
export const MAX_TOOLTIP_ITEMS = 10;

// Time formatting utilities
export const formatTime = (date: string | Date): string =>
  new Date(date).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

export const WEEKDAYS = [
  { key: 'SUNDAY', short: 'S' },
  { key: 'MONDAY', short: 'M' },
  { key: 'TUESDAY', short: 'T' },
  { key: 'WEDNESDAY', short: 'W' },
  { key: 'THURSDAY', short: 'T' },
  { key: 'FRIDAY', short: 'F' },
  { key: 'SATURDAY', short: 'S' },
];

// Constants for recipient display
export const MAX_VISIBLE_ITEMS = 15;

/**
 * Calculates visible items and remaining count for display
 * Can be used for any list of items (recipients, sites, etc.)
 */
export const calculateVisibleItems = (items: string[]) => {
  if (items.length === 0) return { visibleItems: [], remainingCount: 0 };

  if (items.length <= MAX_VISIBLE_ITEMS) {
    return {
      visibleItems: items,
      remainingCount: 0,
    };
  }

  return {
    visibleItems: items.slice(0, MAX_VISIBLE_ITEMS),
    remainingCount: items.length - MAX_VISIBLE_ITEMS,
  };
};

/**
 * Filters items by search value (case-insensitive)
 */
export const filterItemsBySearch = (items: string[], searchValue: string): string[] => {
  if (!searchValue) return items;
  return items.filter((name) => name.toLowerCase().includes(searchValue.toLowerCase()));
};

export const getOccurrenceText = (
  status: ScheduleStatus | undefined,
  occurrence: ReoccurrenceType | string | undefined,
  runTime: string,
  daysOfWeek?: string[] | null,
  dayOfMonth?: number | null,
  t?: TFunction
): string | null => {
  const { DAILY, WEEKLY, MONTHLY, NO_REPEAT } = ReoccurrenceType;
  if (status === ScheduleStatus.EXPIRED) {
    return null;
  }

  const formattedRunTime = dayjs(runTime, 'HH:mm:ss').format('h:mm A');
  if (occurrence === NO_REPEAT) {
    return `One time at ${formattedRunTime}`;
  }

  if (occurrence === DAILY) {
    return `Daily at ${formattedRunTime}`;
  }

  // If Weekly, return "M, W, F at {runtime}"
  if (occurrence === WEEKLY) {
    if (daysOfWeek && daysOfWeek.length > 0) {
      const daysAbbr = daysOfWeek
        .map((day) => {
          const weekday = WEEKDAYS.find((w) => w.key === day);
          return weekday ? weekday.short : day.charAt(0);
        })
        .join(', ');
      return `${daysAbbr} at ${formattedRunTime}`;
    }
    return `Weekly at ${formattedRunTime}`;
  }

  if (occurrence === MONTHLY) {
    if (dayOfMonth) {
      return `Monthly on ${dayOfMonth} at ${formattedRunTime}`;
    }
    return `Monthly at ${formattedRunTime}`;
  }

  return `${occurrence} at ${formattedRunTime}`;
};

// Filter options factories
export const createStatusOptions = (t: TFunction): MultiselectOption[] => [
  { id: 'ACTIVE', label: t('filters.statusOptions.ongoing') },
  { id: 'EXPIRING_SOON', label: t('filters.statusOptions.expiringSoon') },
  { id: 'EXPIRED', label: t('filters.statusOptions.expired') },
];

export const createOccurrenceOptions = (t: TFunction): MultiselectOption[] => [
  { id: 'DAILY', label: t('filters.occurrenceOptions.daily') },
  { id: 'WEEKLY', label: t('filters.occurrenceOptions.weekly') },
  { id: 'MONTHLY', label: t('filters.occurrenceOptions.monthly') },
];

// User name utilities
export const getOrganizationUserFullName = (username: string, organizationUsers?: OrganizationUser[]): string => {
  const user = organizationUsers?.find((u) => u.username === username);
  return user?.fullName ?? '';
};

export const createScheduledByOptionsFromReports = (
  reports: ScheduledReport[],
  organizationUsers: OrganizationUser[]
): MultiselectOption[] => {
  // Get unique scheduledBy users from reports
  const uniqueScheduledByUsers = [...new Set(reports.map((report) => report.scheduledBy))];

  // Map to MultiselectOption format with user names
  return uniqueScheduledByUsers
    .map((username) => ({
      id: username,
      label: getOrganizationUserFullName(username, organizationUsers) || username,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// Tab configuration factory
interface TabConfig {
  label: string;
  id: ScheduledReportsTabId;
}

export const createTabsConfig = (isAdmin: boolean, t: TFunction): TabConfig[] => {
  if (isAdmin) {
    return [
      { label: t('admin.scheduledReports.tabs.all'), id: ScheduledReportsTabId.All },
      { label: t('admin.scheduledReports.tabs.scheduledByYou'), id: ScheduledReportsTabId.ScheduledByYou },
      { label: t('admin.scheduledReports.tabs.sentToYou'), id: ScheduledReportsTabId.SentToYou },
    ];
  }
  return [
    { label: t('admin.scheduledReports.tabs.scheduledByYou'), id: ScheduledReportsTabId.ScheduledByYou },
    { label: t('admin.scheduledReports.tabs.sentToYou'), id: ScheduledReportsTabId.SentToYou },
  ];
};

// Tab filtering utilities
export const filterReportsByTab = (
  reports: ScheduledReport[],
  user: User,
  tabId: ScheduledReportsTabId | undefined
): ScheduledReport[] => {
  const currentUserId = user.profile?.userId ?? '';

  if (tabId === ScheduledReportsTabId.ScheduledByYou) {
    return reports.filter((report) => report.scheduledBy === currentUserId);
  }
  if (tabId === ScheduledReportsTabId.SentToYou) {
    return reports.filter(
      (report) =>
        report.recipients?.some((recipient) => recipient.username === currentUserId) &&
        report.scheduledBy !== currentUserId
    );
  }
  return reports;
};

// Search and filter utilities
interface ReportFilters {
  search: string;
  selectedStatusIds: string[];
  selectedOccurrenceIds: string[];
  selectedSiteIds: string[];
  selectedScheduledByIds: string[];
}

export const filterReports = (reports: ScheduledReport[], filters: ReportFilters): ScheduledReport[] => {
  const { search, selectedStatusIds, selectedOccurrenceIds, selectedSiteIds, selectedScheduledByIds } = filters;
  return reports.filter((report) => {
    // Search filter
    if (search.trim()) {
      const query = search.toLowerCase();
      const matchesSearch =
        report.scheduleName.toLowerCase().includes(query) ||
        report.reportName.toLowerCase().includes(query) ||
        report.scheduledBy.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    // CANCELLED reports should not be visible regardless of other filters
    if (report.scheduleStatus === 'CANCELLED') {
      return false;
    }

    // Status filter
    if (selectedStatusIds.length > 0) {
      const matchesStatus = selectedStatusIds.some((selectedId) => {
        if (selectedId === 'ACTIVE') {
          // ACTIVE filter includes SCHEDULED, ACTIVE, and EXPIRING_SOON (all non-expired)
          return report.scheduleStatus === ScheduleStatus.SCHEDULED || report.scheduleStatus === ScheduleStatus.ACTIVE;
        }
        // For other filters (EXPIRING_SOON, EXPIRED), exact match
        return selectedId === report.scheduleStatus;
      });

      if (!matchesStatus) {
        return false;
      }
    }
    // Occurrence filter
    if (selectedOccurrenceIds.length > 0 && !selectedOccurrenceIds.includes(report.recurrenceType)) {
      return false;
    }

    // Site filter
    if (selectedSiteIds.length > 0) {
      const reportStoreIds = Array.isArray(report.storeIds) ? report.storeIds : [];
      const hasMatchingSite = selectedSiteIds.some((siteId) => reportStoreIds.includes(siteId));
      if (!hasMatchingSite) return false;
    }

    // Scheduled By filter
    if (selectedScheduledByIds.length > 0 && !selectedScheduledByIds.includes(report.scheduledBy)) {
      return false;
    }

    return true;
  });
};

// Check if current user is owner
export const isScheduledByCurrentUser = (scheduledBy: string, user: User): boolean =>
  scheduledBy === user?.profile?.userId;

export const getStatusChipColor = (status: ScheduleStatus): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case ScheduleStatus.ACTIVE:
    case ScheduleStatus.SCHEDULED:
      return 'success';
    case ScheduleStatus.EXPIRING_SOON:
      return 'warning';
    case ScheduleStatus.EXPIRED:
      return 'error';
    default:
      return 'default';
  }
};

export const formatDateRange = (startDate: string, endDate: string): string => {
  const start = dayjs.utc(startDate).format('MM/DD/YYYY');
  if (!endDate) {
    return start;
  }
  const end = dayjs.utc(endDate).format('MM/DD/YYYY');
  return `${start} – ${end}`;
};

export const getSiteNameById = (siteId: string, sites: SiteInfo[] | undefined): string => {
  const site = sites?.find((s) => s.enterpriseUnitId === siteId);
  return site?.name ?? '';
};

/**
 * Converts defaultValue property to displayable string
 * Handles arrays, objects with labels, date ranges, and primitive values
 */
export const getDisplayValueForFilter = (columnName: string, defaultValue?: Record<string, unknown>): string => {
  // Sites are handled separately with special UI
  if (columnName === 'sites') {
    return '';
  }

  // Get the value based on the column name
  const rawValue = defaultValue?.[columnName as keyof typeof defaultValue];

  // Check if value is empty
  if (!rawValue || (Array.isArray(rawValue) && rawValue.length === 0)) {
    return '-';
  }

  // Handle array values
  if (Array.isArray(rawValue)) {
    // Check if array contains objects with label property
    if (rawValue.length > 0 && typeof rawValue[0] === 'object' && rawValue[0] !== null) {
      const firstItem = rawValue[0] as Record<string, unknown>;
      if ('label' in firstItem) {
        return rawValue
          .map((item) => {
            if (typeof item === 'object' && item !== null) {
              const obj = item as Record<string, unknown>;
              return 'label' in obj ? String(obj.label) : String(item);
            }
            return String(item);
          })
          .join(', ');
      }
    }
    // Handle primitive array values
    return rawValue.map((item) => String(item)).join(', ');
  }

  // Handle object values
  if (typeof rawValue === 'object' && rawValue !== null) {
    const objValue = rawValue as Record<string, unknown>;

    // Handle dateRange objects: { startDate, endDate }
    if ('startDate' in objValue && 'endDate' in objValue) {
      const startDate = String(objValue.startDate || '');
      const endDate = String(objValue.endDate || '');
      return startDate && endDate ? `${startDate} - ${endDate}` : '-';
    }
    // Handle OptionType objects: { label }
    if ('label' in objValue && typeof objValue.label === 'string') {
      return objValue.label || '-';
    }
    // Handle other objects - extract meaningful data or return empty
    return '-';
  }

  // Handle primitive values (string, number, boolean)
  return String(rawValue);
};
